import React, { memo } from 'react';
import useModalDialog from 'hooks/util/useModalDialog';
import usePromptDeliveryReport from 'hooks/store/prompts/usePromptDeliveryReport';
import { edit_conflict_close_icon, edit_conflict_warning_icon } from 'utils/images';
import styles from './UnmatchedRecipientsModal.module.scss';

/**
 * IMP-2294 — shown after a Pair Connection send that missed recipients with no active pair.
 * A warning, not an error: the matched recipients were still sent the prompt. Mounted once
 * in Layout and driven by usePromptDeliveryReport, which useSendPromptData fills.
 */
function UnmatchedRecipientsModal() {
  const { report, clearReport } = usePromptDeliveryReport();
  const isOpen = !!report;
  const dialogRef = useModalDialog<HTMLDivElement>(isOpen, clearReport);

  if (!report) return null;

  const count = report.undelivered.length;
  const heading = count === 1 ? '1 recipient is not matched' : `${count} recipients are not matched`;

  return (
    <div className={styles.overlay} role="presentation" onMouseDown={event => event.stopPropagation()}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="unmatched-recipients-title"
        aria-describedby="unmatched-recipients-body">
        <button type="button" className={styles.closeButton} onClick={clearReport} aria-label="Close">
          <img src={edit_conflict_close_icon} alt="" />
        </button>

        <div className={styles.iconBadge}>
          <img src={edit_conflict_warning_icon} alt="" />
        </div>

        <h2 id="unmatched-recipients-title" className={styles.title}>
          {heading}
        </h2>

        <p id="unmatched-recipients-body" className={styles.paragraph}>
          Pair Connection prompts are delivered to a pair chat. These recipients have no active pair, so they did not
          receive this prompt.
        </p>

        <div className={styles.listSection}>
          <h3 id="unmatched-recipients-list-heading" className={styles.listHeading}>
            Not delivered ({count})
          </h3>
          {/* Focusable so keyboard users can scroll a long list */}
          <ul className={styles.list} tabIndex={0} aria-labelledby="unmatched-recipients-list-heading">
            {report.undelivered.map(recipient => (
              <li key={recipient._id} className={styles.listItem}>
                <span className={styles.recipientName}>{recipient.name}</span>
                <span className={styles.recipientRole}>{recipient.role}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className={styles.footer}>
          Sent to {report.deliveredCount} of {report.totalRecipients} recipients.
        </p>

        <div className={styles.actions}>
          <button type="button" className={styles.primaryBtn} onClick={clearReport}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(UnmatchedRecipientsModal);
